import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useState } from "react";
import { PseoPage } from "@/components/laneguide/PseoPage";
import { formatKr, lendersForAmount } from "@/lib/pseo";
import { annuity, effectiveRate, totalCost } from "@/lib/pseo-cost";
import { CAR_MODELS, getCarModel } from "@/lib/pseo-content";

const BASE = "https://svensklaneguide.se";

/** Pilotsida med full E-E-A-T-behandling. Övriga modellsidor följer efter. */
const PILOT_SLUG = "tesla-model-y";

const AUTHOR = {
  name: "Emilio Youssef",
  role: "grundare av Svensk Låneguide, tidigare verksam inom låneförmedling",
  href: "/om-oss/redaktion",
  linkedIn: "https://www.linkedin.com/in/emilio-youssef-a7806230b/",
};

const REVIEWER = {
  name: "Svensk Låneguides redaktion",
  role: "faktagranskare",
  href: "/om-oss/redaktion",
};

/** Datum då räntorna och prisuppgifterna på sidan senast stämdes av. */
const DATA_DATE = "september 2026";

const SETUP_FEE = 495;
const MONTHLY_FEE = 45;

function pct(n: number) {
  return n.toFixed(2).replace(".", ",");
}

/** Alla siffror som citeras på pilotsidan – uträknade från långivardatan. */
function pilotFacts(amount: number) {
  const lenders = lendersForAmount(amount);
  const rate = lenders.length ? Math.min(...lenders.map((l) => l.minInterest)) : 5.95;
  const months = 84;
  const monthly = annuity(amount, months, rate) + MONTHLY_FEE;
  const total = totalCost(amount, months, rate, SETUP_FEE, MONTHLY_FEE);
  const eff = effectiveRate(amount, months, rate, SETUP_FEE, MONTHLY_FEE);
  return { lenders, rate, months, monthly, total, eff, amount };
}

function pilotFaq(name: string, amount: number) {
  const f = pilotFacts(amount);
  return [
    {
      q: `Vad kostar ett billån till ${name} per månad?`,
      a: `Ett billån på ${formatKr(amount)} till en ${name}, återbetalt över 7 år med ${pct(
        f.rate,
      )} procent nominell ränta, kostar cirka ${formatKr(
        f.monthly,
      )} i månaden inklusive aviavgift. Totalt betalar du då ${formatKr(
        f.total,
      )}, varav ${formatKr(f.total - amount)} är ränta och avgifter. Uppgifterna avser ${DATA_DATE} och din ränta sätts individuellt.`,
    },
    {
      q: `Behöver jag kontantinsats för att köpa en ${name}?`,
      a: `Nej. Ett billån utan säkerhet (blancolån) till en ${name} kräver ingen kontantinsats, till skillnad från bilhandlarens egen finansiering som i regel kräver 20 procent kontant och att bilen pantsätts. Du blir kontantköpare och kan förhandla om priset.`,
    },
    {
      q: `Vad är effektiv ränta på ett billån till ${name}?`,
      a: `Med ${formatKr(amount)} över 7 år, ${pct(
        f.rate,
      )} procent nominell ränta, ${formatKr(SETUP_FEE)} i uppläggningsavgift och ${formatKr(
        MONTHLY_FEE,
      )} per avi blir den effektiva räntan ${pct(
        f.eff,
      )} procent. Effektiv ränta räknar in alla avgifter och är det enda måttet som går att jämföra rakt av mellan långivare, enligt konsumentkreditlagen.`,
    },
    {
      q: `Kan jag köpa en ${name} privat på Blocket med lånet?`,
      a: `Ja. Lånen på den här sidan är utan säkerhet, vilket innebär att pengarna betalas ut till ditt eget konto. Du kan därför köpa en ${name} av en privatperson, av en handlare eller importera den – långivaren ställer inga krav på bilen.`,
    },
    {
      q: `Hur lång löptid passar för ett billån till ${name}?`,
      a: `5–7 år är vanligast för ett lånebelopp runt ${formatKr(
        amount,
      )}. Kortare löptid ger högre månadskostnad men klart lägre total räntekostnad, och minskar risken att lånet blir större än bilens värde – en elbil av den här typen tappar mest i värde de första tre åren.`,
    },
    {
      q: `Räknas ${name} som miljöbil vid finansiering?`,
      a: `${name} är en helelektrisk bil och omfattas inte av något statligt inköpsstöd sedan klimatbonusen togs bort i november 2022. Däremot är fordonsskatten låg och driftkostnaden per mil betydligt lägre än för en motsvarande bensinbil, vilket förbättrar din kalkyl när långivaren bedömer din betalningsförmåga.`,
    },
  ];
}

export const Route = createFileRoute("/billan_/$model")({
  loader: ({ params }) => {
    const car = getCarModel(params.model);
    if (!car) throw notFound();
    return { slug: car.slug };
  },
  head: ({ params, loaderData }) => {
    const car = loaderData ? getCarModel(loaderData.slug) : null;
    if (!car) {
      return { meta: [{ title: "Sidan hittades inte" }, { name: "robots", content: "noindex" }] };
    }
    const name = `${car.brand} ${car.model}`;
    const isPilot = car.slug === PILOT_SLUG;
    const f = pilotFacts(car.typicalLoan);
    const title = `Billån ${name} – jämför ränta och månadskostnad 2026`;
    const description = isPilot
      ? `Billån till ${name}: ${formatKr(car.typicalLoan)} över 7 år kostar cirka ${formatKr(
          f.monthly,
        )} i månaden. Jämför ränta, effektiv ränta och långivare.`
      : `Ska du köpa ${name}? Jämför billån utan kontantinsats, se månadskostnad för ${formatKr(car.typicalLoan)} och hitta lägst ränta.`;
    const url = `${BASE}/billan/${params.model}`;

    const faq = isPilot
      ? pilotFaq(name, car.typicalLoan)
      : [
          {
            q: `Hur mycket kostar ett billån till ${name}?`,
            a: `Ett lån på ${formatKr(car.typicalLoan)} över 7 år med 8 procents ränta ger en månadskostnad på cirka ${formatKr(
              annuity(car.typicalLoan, 84, 8),
            )} före avgifter.`,
          },
          {
            q: `Behöver jag kontantinsats för ${name}?`,
            a: "Nej. Ett privatlån utan säkerhet kräver ingen kontantinsats, till skillnad från bilhandlarens egen finansiering som normalt kräver 20 procent.",
          },
        ];

    const scripts: { type: string; children: string }[] = [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: BASE },
            { "@type": "ListItem", position: 2, name: "Billån", item: `${BASE}/billan` },
            { "@type": "ListItem", position: 3, name: name, item: url },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faq.map((q) => ({
            "@type": "Question",
            name: q.q,
            acceptedAnswer: { "@type": "Answer", text: q.a },
          })),
        }),
      },
    ];

    if (isPilot) {
      const person = {
        "@type": "Person",
        name: AUTHOR.name,
        jobTitle: "Grundare och redaktör",
        description: AUTHOR.role,
        url: `${BASE}/om-oss/redaktion`,
        knowsAbout: ["Billån", "Privatlån", "Låneförmedling", "Konsumentkrediter"],
        sameAs: [`${BASE}/om-oss/redaktion`, AUTHOR.linkedIn],
      };
      const org = {
        "@type": "Organization",
        name: "Svensk Låneguide",
        url: BASE,
        logo: `${BASE}/favicon.png`,
      };
      scripts.push(
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: title,
            description,
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
            inLanguage: "sv-SE",
            author: person,
            reviewedBy: {
              "@type": "Organization",
              name: REVIEWER.name,
              url: `${BASE}${REVIEWER.href}`,
            },
            publisher: org,
            about: { "@type": "Car", name, brand: { "@type": "Brand", name: car.brand } },
            citation: [
              "https://www.riksbank.se/sv/statistik/rantor-och-valutakurser/",
              "https://www.konsumentverket.se/for-foretag/marknadsforing/marknadsforing-av-krediter/",
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: `Långivare som förmedlar billån till ${name}`,
            itemListElement: f.lenders.slice(0, 10).map((l, i) => ({
              "@type": "ListItem",
              position: i + 1,
              item: {
                "@type": "FinancialProduct",
                name: `${l.name} billån`,
                provider: { "@type": "Organization", name: l.name },
                annualPercentageRate: l.minInterest,
                amount: {
                  "@type": "MonetaryAmount",
                  currency: "SEK",
                  minValue: l.minAmount,
                  maxValue: l.maxAmount,
                },
              },
            })),
          }),
        },
      );
    }

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "robots", content: "index, follow" },
        ...(isPilot ? [{ name: "author", content: AUTHOR.name }] : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts,
    };
  },
  component: CarLoanPage,
});

function CarLoanPage() {
  const { slug } = Route.useLoaderData();
  const car = getCarModel(slug);
  if (!car) return null;
  const name = `${car.brand} ${car.model}`;
  const isPilot = car.slug === PILOT_SLUG;
  const [amount, setAmount] = useState(car.typicalLoan);
  const lenders = lendersForAmount(amount);
  // Siffrorna i texten följer kalkylatorns belopp så att rubrik, tabell och
  // räkneexempel alltid visar samma sak.
  const f = pilotFacts(amount);

  const related = CAR_MODELS.filter((c) => c.brand === car.brand && c.slug !== car.slug)
    .slice(0, 5)
    .concat(CAR_MODELS.filter((c) => c.brand !== car.brand).slice(0, 5))
    .map((c) => ({ label: `Billån ${c.brand} ${c.model}`, to: `/billan/${c.slug}` }));

  if (!isPilot) {
    return (
      <PseoPage
        h1={`Billån ${name} – jämför ränta och månadskostnad`}
        intro={`${car.note} Priset för en ${name} ligger normalt mellan ${formatKr(car.priceFrom)} och ${formatKr(car.priceTo)}. Nedan jämför du billån utan säkerhet, vilket gör att du kan köpa både av handlare och privat – utan kontantinsats.`}
        amount={amount}
        onAmountChange={setAmount}
        amountEditable
        lenders={lenders}
        crumbs={[{ label: "Hem", to: "/" }, { label: "Billån", to: "/billan" }, { label: name }]}
        epi="pseo-billan"
        costHeading={<>Hur mycket kostar {formatKr(amount)} per månad?</>}
      comparisonHeading={<>Vilka långivare kan finansiera en Tesla Model Y för {formatKr(amount)}?</>}
      comparisonDataDate={DATA_DATE}
      showEffectiveRateInComparison
      affiliateDisclosurePlacement="comparison"
      relatedTitle="Billån till andra modeller"
        related={[...related, { label: "Alla billån", to: "/billan" }]}
        faq={[
          {
            q: `Vad kostar ett billån till ${name} per månad?`,
            a: `Med ett lånebelopp på ${formatKr(car.typicalLoan)}, 7 års löptid och 8 procents ränta blir månadskostnaden cirka ${formatKr(annuity(car.typicalLoan, 84, 8))} före uppläggnings- och aviavgifter. Justera beloppet i kalkylatorn ovan för din bil.`,
          },
          {
            q: "Billån med säkerhet eller blancolån?",
            a: `Billån med bilen som säkerhet har lägre ränta men kräver oftast 20 procents kontantinsats, helförsäkring och att bilen köps av en handlare. Ett blancolån är dyrare i ränta men helt fritt – du kan köpa en ${name} privat och betala kontant på plats.`,
          },
          {
            q: `Kan jag köpa en ${name} på Blocket med lånet?`,
            a: "Ja. Eftersom lånen på den här sidan är utan säkerhet betalas pengarna ut till ditt konto och du agerar kontantköpare, oavsett om säljaren är privatperson eller handlare.",
          },
          {
            q: "Hur lång löptid bör jag välja?",
            a: "Anpassa löptiden efter hur länge du tänker äga bilen. 5–7 år är vanligast; längre löptid sänker månadskostnaden men höjer totalkostnaden och risken att lånet överstiger bilens värde.",
          },
        ]}
        extraContent={
          <section className="mx-auto max-w-3xl px-4 pb-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-2xl font-black text-foreground">Att tänka på när du finansierar en {name}</h2>
            <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
              Utöver månadskostnaden för lånet tillkommer försäkring, skatt, service och däck. Räkna på den totala
              månadskostnaden innan du bestämmer lånebelopp – en tumregel är att bilens totala kostnad inte bör överstiga
              15 procent av hushållets nettoinkomst.
            </p>
            <h3 className="mt-6 font-display text-lg font-bold text-foreground">Jämför alltid handlarens erbjudande</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
              Bilhandlarens finansiering ser ofta billig ut i månadskostnad men innehåller restvärde och bindningstid. Ta
              fram ett bud via en{" "}
              <Link to="/privatlan" className="text-primary underline underline-offset-2">
                låneförmedlare
              </Link>{" "}
              först, så vet du vad du kan förhandla emot.
            </p>
          </section>
        }
      />
    );
  }

  return (
    <PseoPage
      h1={`Billån Tesla Model Y – vad det kostar per månad 2026`}
      intro={`En Tesla Model Y kostar i dag mellan ${formatKr(car.priceFrom)} och ${formatKr(
        car.priceTo,
      )} på den svenska marknaden (${DATA_DATE}). Ett billån utan säkerhet gör dig till kontantköpare: du slipper kontantinsats, bilen behöver inte pantsättas och du kan köpa av handlare, privatperson eller importera.`}
      amount={amount}
      onAmountChange={setAmount}
      amountEditable
      defaultYears={7}
      lenders={lenders}
      crumbs={[{ label: "Hem", to: "/" }, { label: "Billån", to: "/billan" }, { label: name }]}
      epi="pseo-billan"
      byline={{
        author: AUTHOR.name,
        authorRole: AUTHOR.role,
        authorHref: AUTHOR.href,
        reviewer: REVIEWER.name,
        reviewerRole: REVIEWER.role,
      }}
      costHeading={<>Hur mycket kostar {formatKr(amount)} per månad?</>}
      comparisonHeading={<>Vilka långivare kan finansiera en Tesla Model Y för {formatKr(amount)}?</>}
      comparisonDataDate={DATA_DATE}
      showEffectiveRateInComparison
      affiliateDisclosurePlacement="comparison"
      relatedTitle="Billån till andra modeller"
      related={[
        ...related,
        { label: "Alla billån", to: "/billan" },
        { label: "Lånekalkylator", to: "/guider/lanekalkyl" },
      ]}
      leadAnswer={
        <>
          <p>
            Ett billån på {formatKr(amount)} till en Tesla Model Y kostar cirka{" "}
            <strong>{formatKr(f.monthly)} i månaden</strong> vid 7 års återbetalningstid och {pct(f.rate)} procent
            nominell ränta, inklusive {formatKr(MONTHLY_FEE)} i aviavgift. Totalt betalar du {formatKr(f.total)},
            varav {formatKr(f.total - amount)} är ränta och avgifter. Effektiv ränta blir {pct(f.eff)}{" "}
            procent. Uppgifterna avser {DATA_DATE}.
          </p>
          <p>
            Lånet är ett blancolån utan säkerhet, vilket betyder att ingen kontantinsats krävs och att Tesla Model Y
            inte pantsätts. {f.lenders.length} av långivarna i vår jämförelse förmedlar {formatKr(amount)}. Din
            faktiska ränta sätts individuellt utifrån inkomst och kreditvärdighet.
          </p>
        </>
      }
      faq={pilotFaq(name, car.typicalLoan)}
      sources={[
        {
          label: "Riksbanken – räntor och valutakurser (utlåningsräntor till hushåll)",
          href: "https://www.riksbank.se/sv/statistik/rantor-och-valutakurser/",
        },
        {
          label: "Konsumentverket – marknadsföring av krediter och måttfullhetskravet",
          href: "https://www.konsumentverket.se/for-foretag/marknadsforing/marknadsforing-av-krediter/",
        },
        {
          label: "Konsumentkreditlag (2010:1846) om effektiv ränta – Sveriges riksdag",
          href: "https://www.riksdagen.se/sv/dokument-och-lagar/dokument/svensk-forfattningssamling/konsumentkreditlag-20101846_sfs-2010-1846/",
        },
        {
          label: "Kronofogden – om betalningsanmärkningar och skuldsanering",
          href: "https://kronofogden.se/",
        },
      ]}
      extraContent={
        <section className="mx-auto max-w-3xl px-4 pb-6 sm:px-6 lg:px-8">
          <div className="rounded-md border border-border bg-card p-5">
            <h2 className="font-display text-lg font-bold text-foreground">
              Hur ser ett representativt räkneexempel för Tesla Model Y ut?
            </h2>
            <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
              Annuitetslån {formatKr(amount)}, återbetalningstid 7 år (84 betalningar), nominell ränta{" "}
              {pct(f.rate)} procent (rörlig), uppläggningsavgift {formatKr(SETUP_FEE)}, aviavgift{" "}
              {formatKr(MONTHLY_FEE)} per månad. Effektiv ränta {pct(f.eff)} procent. Månadskostnad{" "}
              {formatKr(f.monthly)}. Totalt att betala {formatKr(f.total)}. Exemplet gäller {DATA_DATE} och avser den
              lägsta annonserade räntan bland de långivare vi jämför – räntan sätts individuellt och kan bli högre.
            </p>
          </div>

          <h2 className="mt-10 font-display text-2xl font-black text-foreground">
            Vad kostar en Tesla Model Y att äga utöver lånet?
          </h2>
          <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
            Månadskostnaden för lånet är bara en del av kalkylen för en Tesla Model Y. Räkna även med helförsäkring,
            fordonsskatt, däck, service och laddning. En elbil av Model Y:s storlek drar cirka 1,8–2,2 kWh per mil,
            vilket vid hemmaladdning motsvarar ungefär en tredjedel av drivmedelskostnaden för en jämförbar
            bensin-SUV. En vanlig tumregel bland låneförmedlare är att bilens totala månadskostnad inte bör överstiga
            15 procent av hushållets nettoinkomst.
          </p>

          <h2 className="mt-10 font-display text-2xl font-black text-foreground">
            Blancolån eller bilhandlarens finansiering till Tesla Model Y?
          </h2>
          <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
            Bilhandlarens eget avtal har ofta lägre nominell ränta, men kräver normalt 20 procent kontantinsats,
            helförsäkring och att bilen står som säkerhet. Många upplägg har dessutom ett restvärde som ska lösas vid
            avtalets slut. Ett blancolån på {formatKr(amount)} har högre ränta men inga sådana villkor: du
            äger bilen från dag ett, kan lösa lånet i förtid utan avgift och kan sälja bilen när du vill. Ta alltid
            fram ett lånebesked först – då vet du vad du förhandlar emot i bilhallen.
          </p>
          <h3 className="mt-6 font-display text-lg font-bold text-foreground">
            Vilka nackdelar bör du känna till innan du ansöker?
          </h3>
          <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
            Ett blancolån är dyrare än ett lån med säkerhet, och en Tesla Model Y tappar mest i värde de tre första
            åren. Väljer du 12 års löptid för att pressa månadskostnaden riskerar du att skulden överstiger bilens
            värde större delen av innehavstiden. Ansöker du hos flera långivare direkt registreras dessutom flera
            kreditupplysningar – gå via en{" "}
            <Link to="/privatlan" className="text-primary underline underline-offset-2">
              låneförmedlare
            </Link>{" "}
            som tar en enda upplysning i stället.
          </p>

          <h2 className="mt-10 font-display text-2xl font-black text-foreground">
            Hur tar vi fram siffrorna på den här sidan?
          </h2>
          <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
            Räntorna kommer från långivarnas egna publicerade prislistor och stäms av löpande, senast{" "}
            {DATA_DATE}. Kostnadsexemplen räknas som annuitetslån och den effektiva räntan beräknas enligt
            konsumentkreditlagens modell, med uppläggningsavgift och aviavgift inräknade. Sidan är skriven av{" "}
            {AUTHOR.name}, som tidigare arbetat inom låneförmedling. Läs mer om{" "}
            <Link to="/om-oss/hur-vi-granskar" className="text-primary underline underline-offset-2">
              hur vi granskar långivare
            </Link>
            .
          </p>
        </section>
      }
    />
  );
}
